/**
 * Embedded Mode Handler
 * 
 * Handles postMessage communication between the voice app (iframe) and parent window (text chatbot).
 * Provides a clean API for:
 * - Receiving commands from parent (launch, context_update, close)
 * - Sending status updates to parent (ready, status, result, error)
 */

import { logger } from '../utils/logger';

// Message types from parent to iframe
export interface ParentMessage {
  type: 'launch' | 'context_update' | 'close';
  agent?: string;
  context?: Record<string, any>;
  language?: string;
}

// Message types from iframe to parent
export interface VoiceMessage {
  type: 'voice_ready' | 'voice_status' | 'voice_result' | 'voice_error' | 'voice_close';
  status?: 'idle' | 'listening' | 'speaking';
  result?: any;
  error?: string;
  agent?: string;
}

// Callback types
export type LaunchCallback = (agent: string, context?: Record<string, any>) => void;
export type ContextUpdateCallback = (context: Record<string, any>) => void;
export type CloseCallback = () => void;

export class EmbeddedModeHandler {
  private isEmbedded: boolean;
  private allowedOrigins: string[];
  private messageHandlers: Map<string, Function>;
  
  constructor(isEmbedded: boolean = false, allowedOrigins: string[] = ['*']) {
    this.isEmbedded = isEmbedded;
    this.allowedOrigins = allowedOrigins;
    this.messageHandlers = new Map();
    
    if (this.isEmbedded) {
      this.initialize();
    }
  }
  
  /**
   * Initialize postMessage listener
   */
  private initialize() {
    window.addEventListener('message', this.handleParentMessage.bind(this));
    logger.info('[Embedded] Handler initialized, listening for parent messages');
    
    // Notify parent that iframe is ready
    this.sendReady();
  }
  
  /**
   * Handle incoming messages from parent window
   */
  private handleParentMessage(event: MessageEvent) {
    // Validate origin for security
    if (!this.isOriginAllowed(event.origin)) {
      logger.warn('[Embedded] Message from unauthorized origin:', event.origin);
      return;
    }
    
    const message = event.data as ParentMessage;
    
    logger.info('[Embedded] Received message from parent:', message.type);
    
    switch (message.type) {
      case 'launch':
        if (message.agent) {
          const callback = this.messageHandlers.get('launch');
          if (callback) {
            callback(message.agent, message.context);
          }
        }
        break;
        
      case 'context_update':
        if (message.context) {
          const callback = this.messageHandlers.get('context_update');
          if (callback) {
            callback(message.context);
          }
        }
        break;
        
      case 'close':
        const callback = this.messageHandlers.get('close');
        if (callback) {
          callback();
        }
        break;
        
      default:
        logger.warn('[Embedded] Unknown message type:', message.type);
    }
  }
  
  /**
   * Check if origin is allowed
   */
  private isOriginAllowed(origin: string): boolean {
    // Allow all origins if '*' is in the list (development mode)
    if (this.allowedOrigins.includes('*')) {
      return true;
    }
    
    return this.allowedOrigins.includes(origin);
  }
  
  /**
   * Send message to parent window
   */
  private sendToParent(message: VoiceMessage) {
    if (!this.isEmbedded || !window.parent) {
      return;
    }
    
    try {
      window.parent.postMessage(message, '*'); // Parent should validate origin
      logger.info('[Embedded] Sent message to parent:', message.type);
    } catch (error) {
      logger.error('[Embedded] Failed to send message to parent:', error);
    }
  }
  
  /**
   * Register callback for launch events
   */
  public onLaunch(callback: LaunchCallback) {
    this.messageHandlers.set('launch', callback);
  }
  
  /**
   * Register callback for context update events
   */
  public onContextUpdate(callback: ContextUpdateCallback) {
    this.messageHandlers.set('context_update', callback);
  }
  
  /**
   * Register callback for close events
   */
  public onClose(callback: CloseCallback) {
    this.messageHandlers.set('close', callback);
  }
  
  /**
   * Send 'ready' status to parent
   */
  public sendReady() {
    this.sendToParent({ type: 'voice_ready' });
  }
  
  /**
   * Send status update to parent (idle, listening, speaking)
   */
  public sendStatus(status: 'idle' | 'listening' | 'speaking', agent?: string) {
    this.sendToParent({ 
      type: 'voice_status', 
      status, 
      agent 
    });
  }
  
  /**
   * Send flow completion result to parent
   */
  public sendResult(result: any, agent?: string) {
    this.sendToParent({ 
      type: 'voice_result', 
      result, 
      agent 
    });
  }
  
  /**
   * Send error to parent
   */
  public sendError(error: string) {
    this.sendToParent({ 
      type: 'voice_error', 
      error 
    });
  }
  
  /**
   * Send close notification to parent
   */
  public sendClose() {
    this.sendToParent({ type: 'voice_close' });
  }
  
  /**
   * Cleanup event listeners
   */
  public destroy() {
    window.removeEventListener('message', this.handleParentMessage.bind(this));
    this.messageHandlers.clear();
    logger.info('[Embedded] Handler destroyed');
  }
}

// Singleton instance
let handlerInstance: EmbeddedModeHandler | null = null;

/**
 * Get or create the embedded mode handler instance
 */
export function getEmbeddedHandler(isEmbedded: boolean = false, allowedOrigins: string[] = ['*']): EmbeddedModeHandler {
  if (!handlerInstance) {
    handlerInstance = new EmbeddedModeHandler(isEmbedded, allowedOrigins);
  }
  return handlerInstance;
}

/**
 * Cleanup the embedded handler
 */
export function cleanupEmbeddedHandler() {
  if (handlerInstance) {
    handlerInstance.destroy();
    handlerInstance = null;
  }
}
